//#region IMPORT
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../Reducer/SnackbarReducer';
import Global from '../../Services/Common/Global';
import Commons from '../../Services/Common/Common';
import { Loading } from '../../Components/MUIs';
import M_EXT_SIGN_BASIC from './M_EXT_SIGN_BASIC';
import { useSearch2, useSearch3, useSave2, useSave3 } from './M_EXT_SIGN_Query';
//#endregion

export default function M_EXT_SIGN_BASICF() {
  //#region 변수
  const { id, tel } = useParams();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [signInputs, setSignInputs] = useState({ mapUrl: '', mapFiles: {} });
  const [inputs, setInputs] = useState({});
  const [consentChecked, setConsentChecked] = useState(false);
  /** 서버에 등록된 서명을 지운 뒤 재서명 중 */
  const [isEditingSign, setIsEditingSign] = useState(false);

  const signatureCanvas = useRef(null);
  //#endregion

  //#region 쿼리
  const { runAsync: Search2Async } = useSearch2();
  const { runAsync: Search3Async } = useSearch3();
  const { runAsync: Save2Async } = useSave2();
  const { runAsync: Save3Async } = useSave3();
  //#endregion

  useEffect(() => {
    Init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Init = async () => {
    setLoading(true);
    try {
      await SearchLogic1();
    } catch (e) {
      console.log('Error: ', e);
      dispatch(
        showSnackbar({
          message: e.message,
          type: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  //#region 조회
  /** 회의록 상세 조회 (GetMetBasic01R2 / R3) — URL tel 기준 */
  const SearchLogic1 = async () => {
    const telFromUrl = NormalizeTel(tel);
    ResetSignCanvas();
    setInputs({});
    setConsentChecked(false);
    setIsEditingSign(false);

    const chaebun = (id || '').toString().substring(1);
    const isC = (id || '').toString().indexOf('C') === 0;

    if (!chaebun) {
      return;
    }

    try {
      const context = { chaebun, tel: telFromUrl };
      const data = isC
        ? await Search2Async(context)
        : await Search3Async(context);

      const rows = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : data
            ? [data]
            : [];

      if (rows.length === 0) {
        dispatch(
          showSnackbar({
            message: '회의 정보를 불러오지 못했습니다.',
            type: 'warning',
          })
        );
        return;
      }

      const matched = rows.find((row) => NormalizeTel(row.tel) === telFromUrl);

      if (!matched) {
        dispatch(
          showSnackbar({
            message: '연락처에 해당하는 참석자 정보를 찾을 수 없습니다.',
            type: 'warning',
          })
        );
        return;
      }

      setInputs(matched);

      if (IsSignCompleted(matched)) {
        dispatch(
          showSnackbar({
            message: '이미 서명이 완료된 회의입니다.',
            type: 'warning',
          })
        );
        setConsentChecked(true);
        await LoadExistingSignature(matched, isC);
      }
    } catch (e) {
      console.log(e);
    }
  };
  //#endregion

  //#region 저장
  /** 서명 완료: 참석자 서명 (SaveMetBasic01U2~U3) */
  const SaveLogic1 = async () => {
    const name = inputs.name;
    const custCd = inputs.custCd;

    if (!name) {
      dispatch(
        showSnackbar({
          message: '성명을 입력해 주세요.',
          type: 'warning',
        })
      );
      return;
    }
    if (!custCd) {
      dispatch(
        showSnackbar({
          message: '사업장 정보를 확인할 수 없습니다.',
          type: 'error',
        })
      );
      return;
    }
    if (!consentChecked) {
      dispatch(
        showSnackbar({
          message: '서명 정보 수집·이용에 동의해 주세요.',
          type: 'warning',
        })
      );
      return;
    }

    const chaebun = id?.substring(1) ?? '';
    const isC = (id || '').toString().indexOf('C') === 0;

    if (!chaebun) {
      dispatch(
        showSnackbar({
          message: '유효하지 않은 링크입니다.',
          type: 'error',
        })
      );
      return;
    }

    try {
      const hasFile =
        signInputs.mapFiles &&
        (signInputs.mapFiles.size > 0 || signInputs.mapFiles.name);
      if (!hasFile) {
        dispatch(
          showSnackbar({ message: '서명을 진행하세요.', type: 'warning' })
        );
        return;
      }

      const savedFilePath = await SaveLogic2(signInputs.mapFiles);
      const fileName = signInputs.mapFiles.name || '';
      const filePath = isC
        ? `Business/${custCd}/SUB_BASIC_03/${fileName}`
        : `Business/${custCd}/SAC_BASIC/${fileName}`;

      const payload = {
        chaebun,
        checkYn: 'Y',
        fileName,
        filePath: savedFilePath ? filePath : '',
        name,
        depNm: inputs.depNm,
        tel: NormalizeTel(inputs.tel),
      };

      const res = isC
        ? await Save2Async({ row: payload })
        : await Save3Async({ row: payload });

      if (res?.[0]?.type === 'Error') {
        dispatch(showSnackbar({ message: res[0].message, type: 'error' }));
        return;
      }
      dispatch(
        showSnackbar({ message: '서명이 완료되었습니다.', type: 'success' })
      );

      SearchLogic1();
    } catch (e) {
      console.log(e);
      dispatch(
        showSnackbar({
          message: e.message,
          type: e.code === 'CUSTOM_ERROR' ? e.type : 'error',
        })
      );
    }
  };

  // 서명 파일 저장
  const SaveLogic2 = async (_file) => {
    setLoading(true);

    let filePath = '';
    let finalSavePath = '';

    if (id.indexOf('C') === 0) {
      filePath = `Business/${inputs.custCd}/SUB_BASIC_03`;
    } else {
      filePath = `Business/${inputs.custCd}/SAC_BASIC`;
    }

    try {
      const formList = new FormData();
      formList.append('UserId', 'EXT');
      formList.append('FilePath', filePath);
      formList.append('FormFile', _file);

      const result = await Commons.Axios(
        'file2',
        Global.file.useUrl + Global.urls.Execute.videoUpload,
        null,
        formList
      );

      finalSavePath = result?.data?.filePath || '';

      if (finalSavePath) {
        return finalSavePath
          .replace(/^.*\\PreviewFiles/, 'PreviewFiles')
          .replace(/\\/g, '/');
      }

      dispatch(
        showSnackbar({
          message: '파일 업로드 실패 또는 파일 경로 없음',
          type: 'error',
        })
      );
      return null;
    } catch (e) {
      console.error('SaveLogic2 Error:', e);
      return null;
    } finally {
      setLoading(false);
    }
  };
  //#endregion

  //#region 나머지 함수
  const IsSignCompleted = (row) => Boolean(row?.fileName ?? '');

  const ResolveSignFilePath = (row, isC) => {
    const filePath = row?.filePath ?? '';
    if (filePath) return filePath;
    const fileName = row?.fileName ?? '';
    const custCd = row?.custCd ?? '';
    if (!fileName || !custCd) return '';
    const folder = isC ? 'SUB_BASIC_03' : 'SAC_BASIC';
    return `Business/${custCd}/${folder}/${fileName}`;
  };

  const LoadExistingSignature = async (row, isC) => {
    const filePath = ResolveSignFilePath(row, isC);
    if (!filePath) return;

    try {
      const imageUrl = Global.file.useUrl + 'files/' + filePath;
      const response = await fetch(imageUrl, { mode: 'cors' });
      if (!response.ok) return;
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignInputs({ mapUrl: reader.result, mapFiles: {} });
      };
      reader.readAsDataURL(blob);
    } catch (e) {
      console.log(e);
    }
  };

  const ResetSignCanvas = () => {
    signatureCanvas.current?.clear?.();
    signatureCanvas.current?.on?.();
    setSignInputs({ mapUrl: '', mapFiles: {} });
  };

  const NormalizeTel = (value) =>
    decodeURIComponent(String(value ?? '')).replace(/\D/g, '');

  const HandleClearSign = () => {
    ResetSignCanvas();
    setIsEditingSign(true);
  };
  //#endregion

  return (
    <div className="wrap">
      <M_EXT_SIGN_BASIC
        id={id}
        inputs={inputs}
        consentChecked={consentChecked}
        setConsentChecked={setConsentChecked}
        signatureCanvas={signatureCanvas}
        signInputs={signInputs}
        setSignInputs={setSignInputs}
        signRegistered={IsSignCompleted(inputs)}
        isEditingSign={isEditingSign}
        onClear={HandleClearSign}
        onConfirm={SaveLogic1}
      />
      <Loading open={loading} />
    </div>
  );
}
